// --------------------------------------------------------------------------------
// Enums
// --------------------------------------------------------------------------------

/**
 * @example
 * ```graphql
 * enum Channel {
 *   business
 *   leisure
 * }
 * ```
 */
export const Channel = Object.freeze({
  business: 'business',
  leisure: 'leisure'
});

/**
 * @example
 * ```graphql
 * enum DomainId {
 *   """EYZ"""
 *   eyz
 * 
 *   """Hoteljobs"""
 *   hoteljobs_zuerich_ch
 * 
 *   """Meeting"""
 *   meeting_zuerich_com
 * 
 *   """Rapperswil"""
 *   rapperswil_zuerichsee_ch
 * 
 *   """ZT"""
 *   zt_zuerich_com
 * 
 *   """Zürich"""
 *   zuerich_com
 * 
 *   """Zuerich Experience"""
 *   zuerich_experience_com
 * }
 * ```
 */
export const DomainId = Object.freeze({
  /** EYZ */
  eyz: 'eyz',
  /** Hoteljobs */
  hoteljobs_zuerich_ch: 'hoteljobs_zuerich_ch',
  /** Meeting */
  meeting_zuerich_com: 'meeting_zuerich_com',
  /** Rapperswil */
  rapperswil_zuerichsee_ch: 'rapperswil_zuerichsee_ch',
  /** ZT */
  zt_zuerich_com: 'zt_zuerich_com',
  /** Zürich */
  zuerich_com: 'zuerich_com',
  /** Zuerich Experience */
  zuerich_experience_com: 'zuerich_experience_com'
});

/**
 * @example
 * ```graphql
 * enum EntityQueryOperator {
 *   BETWEEN
 *   CONTAINS
 *   ENDS_WITH
 *   EQUAL
 *   GREATER_THAN
 *   GREATER_THAN_OR_EQUAL
 *   IN
 *   IS_NOT_NULL
 *   IS_NULL
 *   LIKE
 *   NOT_BETWEEN
 *   NOT_EQUAL
 *   NOT_IN
 *   NOT_LIKE
 *   REGEXP
 *   SMALLER_THAN
 *   SMALLER_THAN_OR_EQUAL
 *   STARTS_WITH
 * }
 * ```
 */
export const EntityQueryOperator = Object.freeze({
  BETWEEN: 'BETWEEN',
  CONTAINS: 'CONTAINS',
  ENDS_WITH: 'ENDS_WITH',
  EQUAL: 'EQUAL',
  GREATER_THAN: 'GREATER_THAN',
  GREATER_THAN_OR_EQUAL: 'GREATER_THAN_OR_EQUAL',
  IN: 'IN',
  IS_NOT_NULL: 'IS_NOT_NULL',
  IS_NULL: 'IS_NULL',
  LIKE: 'LIKE',
  NOT_BETWEEN: 'NOT_BETWEEN',
  NOT_EQUAL: 'NOT_EQUAL',
  NOT_IN: 'NOT_IN',
  NOT_LIKE: 'NOT_LIKE',
  REGEXP: 'REGEXP',
  SMALLER_THAN: 'SMALLER_THAN',
  SMALLER_THAN_OR_EQUAL: 'SMALLER_THAN_OR_EQUAL',
  STARTS_WITH: 'STARTS_WITH'
});

/**
 * @example
 * ```graphql
 * enum EntityType {
 *   """Content block"""
 *   BLOCK_CONTENT
 * 
 *   """Comment"""
 *   COMMENT
 * 
 *   """Language"""
 *   CONFIGURABLE_LANGUAGE
 * 
 *   """Domain record"""
 *   DOMAIN
 * 
 *   """Feature flag"""
 *   FEATUREFLAG
 * 
 *   """File"""
 *   FILE
 * 
 *   """Media"""
 *   MEDIA
 * 
 *   """Menu"""
 *   MENU
 * 
 *   """Custom menu link"""
 *   MENU_LINK_CONTENT
 * 
 *   """Content"""
 *   NODE
 * 
 *   """Paragraph"""
 *   PARAGRAPH
 * 
 *   """Paragraphs Blokkli Edit State"""
 *   PARAGRAPHS_BLOKKLI_EDIT_STATE
 * 
 *   """Paragraphs library item"""
 *   PARAGRAPHS_LIBRARY_ITEM
 * 
 *   """Paragraphs type"""
 *   PARAGRAPHS_TYPE
 * 
 *   """Redirect"""
 *   REDIRECT
 * 
 *   """Storage"""
 *   STORAGE
 * 
 *   """Taxonomy term"""
 *   TAXONOMY_TERM
 * 
 *   """Translatable config pages"""
 *   TRANSLATABLE_CONFIG_PAGES
 * 
 *   """User"""
 *   USER
 * 
 *   """Webform"""
 *   WEBFORM
 * }
 * ```
 */
export const EntityType = Object.freeze({
  /** Content block */
  BLOCK_CONTENT: 'BLOCK_CONTENT',
  /** Comment */
  COMMENT: 'COMMENT',
  /** Language */
  CONFIGURABLE_LANGUAGE: 'CONFIGURABLE_LANGUAGE',
  /** Domain record */
  DOMAIN: 'DOMAIN',
  /** Feature flag */
  FEATUREFLAG: 'FEATUREFLAG',
  /** File */
  FILE: 'FILE',
  /** Media */
  MEDIA: 'MEDIA',
  /** Menu */
  MENU: 'MENU',
  /** Custom menu link */
  MENU_LINK_CONTENT: 'MENU_LINK_CONTENT',
  /** Content */
  NODE: 'NODE',
  /** Paragraph */
  PARAGRAPH: 'PARAGRAPH',
  /** Paragraphs Blokkli Edit State */
  PARAGRAPHS_BLOKKLI_EDIT_STATE: 'PARAGRAPHS_BLOKKLI_EDIT_STATE',
  /** Paragraphs library item */
  PARAGRAPHS_LIBRARY_ITEM: 'PARAGRAPHS_LIBRARY_ITEM',
  /** Paragraphs type */
  PARAGRAPHS_TYPE: 'PARAGRAPHS_TYPE',
  /** Redirect */
  REDIRECT: 'REDIRECT',
  /** Storage */
  STORAGE: 'STORAGE',
  /** Taxonomy term */
  TAXONOMY_TERM: 'TAXONOMY_TERM',
  /** Translatable config pages */
  TRANSLATABLE_CONFIG_PAGES: 'TRANSLATABLE_CONFIG_PAGES',
  /** User */
  USER: 'USER',
  /** Webform */
  WEBFORM: 'WEBFORM'
});

/**
 * @example
 * ```graphql
 * enum FeatureflagId {
 *   """Enable the newsletter checkbox in the card shop"""
 *   card_shop_newsletter
 * 
 *   """
 *   Enable the "currently open" indicator and "currently open" filter on filter pages
 *   """
 *   currently_open
 * 
 *   """Enable the events widget on the detail page of a place"""
 *   discover_swiss_events
 * 
 *   """Enable the global search in the main menu"""
 *   global_search
 * 
 *   """Enable the "nearby nodes" teaser list on place detail pages."""
 *   nearby_nodes
 * }
 * ```
 */
export const FeatureflagId = Object.freeze({
  /** Enable the newsletter checkbox in the card shop */
  card_shop_newsletter: 'card_shop_newsletter',
  /** Enable the "currently open" indicator and "currently open" filter on filter pages */
  currently_open: 'currently_open',
  /** Enable the events widget on the detail page of a place */
  discover_swiss_events: 'discover_swiss_events',
  /** Enable the global search in the main menu */
  global_search: 'global_search',
  /** Enable the "nearby nodes" teaser list on place detail pages. */
  nearby_nodes: 'nearby_nodes'
});

/**
 * @example
 * ```graphql
 * enum FeatureflagStatus {
 *   """The feature is disabled."""
 *   disabled
 * 
 *   """No decision has been made yet."""
 *   empty
 * 
 *   """The feature is enabled."""
 *   enabled
 * }
 * ```
 */
export const FeatureflagStatus = Object.freeze({
  /** The feature is disabled. */
  disabled: 'disabled',
  /** No decision has been made yet. */
  empty: 'empty',
  /** The feature is enabled. */
  enabled: 'enabled'
});

/**
 * @example
 * ```graphql
 * enum Langcode {
 *   """German"""
 *   DE
 * 
 *   """English"""
 *   EN
 * 
 *   """French"""
 *   FR
 * 
 *   """Italian"""
 *   IT
 * }
 * ```
 */
export const Langcode = Object.freeze({
  /** German */
  DE: 'DE',
  /** English */
  EN: 'EN',
  /** French */
  FR: 'FR',
  /** Italian */
  IT: 'IT'
});

/**
 * @example
 * ```graphql
 * enum ParagraphsBlokkliEditableFieldConfigType {
 *   frame
 *   plain
 * }
 * ```
 */
export const ParagraphsBlokkliEditableFieldConfigType = Object.freeze({
  frame: 'frame',
  plain: 'plain'
});

/**
 * @example
 * ```graphql
 * enum ParagraphsBlokkliRemoteVideoProvider {
 *   VIMEO
 *   YOUTUBE
 * }
 * ```
 */
export const ParagraphsBlokkliRemoteVideoProvider = Object.freeze({
  VIMEO: 'VIMEO',
  YOUTUBE: 'YOUTUBE'
});

/**
 * @example
 * ```graphql
 * enum PlaceClassification {
 *   bars_lounges
 *   cafe
 *   culture
 *   food_drinks
 *   nature
 *   nightlife
 *   recreation
 *   restaurant
 *   shopping
 *   sightseeing
 *   sport
 *   venue
 *   wellness
 * }
 * ```
 */
export const PlaceClassification = Object.freeze({
  bars_lounges: 'bars_lounges',
  cafe: 'cafe',
  culture: 'culture',
  food_drinks: 'food_drinks',
  nature: 'nature',
  nightlife: 'nightlife',
  recreation: 'recreation',
  restaurant: 'restaurant',
  shopping: 'shopping',
  sightseeing: 'sightseeing',
  sport: 'sport',
  venue: 'venue',
  wellness: 'wellness'
});

/**
 * @example
 * ```graphql
 * enum WebformStateConditionTrigger {
 *   """Between"""
 *   BETWEEN
 * 
 *   """Checked"""
 *   CHECKED
 * 
 *   """Empty"""
 *   EMPTY
 * 
 *   """Filled"""
 *   FILLED
 * 
 *   """Greater than"""
 *   GREATER_THAN
 * 
 *   """Greater than/Equal to"""
 *   GREATER_THAN_EQUAL_TO
 * 
 *   """Less than"""
 *   LESS_THAN
 * 
 *   """Less than/Equal to"""
 *   LESS_THAN_EQUAL_TO
 * 
 *   """Not between"""
 *   NOT_BETWEEN
 * 
 *   """Not Pattern"""
 *   NOT_PATTERN
 * 
 *   """Pattern"""
 *   PATTERN
 * 
 *   """Unchecked"""
 *   UNCHECKED
 * 
 *   """Value is"""
 *   VALUE_IS
 * 
 *   """Value is not"""
 *   VALUE_IS_NOT
 * }
 * ```
 */
export const WebformStateConditionTrigger = Object.freeze({
  /** Between */
  BETWEEN: 'BETWEEN',
  /** Checked */
  CHECKED: 'CHECKED',
  /** Empty */
  EMPTY: 'EMPTY',
  /** Filled */
  FILLED: 'FILLED',
  /** Greater than */
  GREATER_THAN: 'GREATER_THAN',
  /** Greater than/Equal to */
  GREATER_THAN_EQUAL_TO: 'GREATER_THAN_EQUAL_TO',
  /** Less than */
  LESS_THAN: 'LESS_THAN',
  /** Less than/Equal to */
  LESS_THAN_EQUAL_TO: 'LESS_THAN_EQUAL_TO',
  /** Not between */
  NOT_BETWEEN: 'NOT_BETWEEN',
  /** Not Pattern */
  NOT_PATTERN: 'NOT_PATTERN',
  /** Pattern */
  PATTERN: 'PATTERN',
  /** Unchecked */
  UNCHECKED: 'UNCHECKED',
  /** Value is */
  VALUE_IS: 'VALUE_IS',
  /** Value is not */
  VALUE_IS_NOT: 'VALUE_IS_NOT'
});

/**
 * @example
 * ```graphql
 * enum WebformStateLogic {
 *   AND
 *   OR
 *   XOR
 * }
 * ```
 */
export const WebformStateLogic = Object.freeze({
  AND: 'AND',
  OR: 'OR',
  XOR: 'XOR'
});